import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Chip, Grid } from "@mui/material";
import { Visibility, School } from "@mui/icons-material";
import { Form, Formik, useFormikContext } from "formik";

import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import Loader from "components/loader";
import FormikInput from "components/formikInput";
import PageHeader from "./components/PageHeader";
import FilterBar, { FilterItem } from "./components/FilterBar";
import EmptyStateCard from "./components/EmptyStateCard";
import CourseProgressBar from "./components/CourseProgressBar";

import { useCourse } from "hooks/useCourse";
import useAuth from "hooks/useAuth";
import { COMPCHECK_URL, userApi } from "configs/api";

interface UserOption {
  _id: string;
  name?: string;
  email?: string;
}

interface FilterValues {
  companyID: string;
  branchID: string;
  employeeID: string;
}

function FiltersObserver({
  onChange,
}: {
  onChange: (name: keyof FilterValues, value: string) => void;
}) {
  const { values } = useFormikContext<FilterValues>();
  const prev = useRef<FilterValues>({ companyID: "", branchID: "", employeeID: "" });

  useEffect(() => {
    const p = prev.current;
    if (p.companyID !== values.companyID) onChange("companyID", values.companyID);
    if (p.branchID !== values.branchID) onChange("branchID", values.branchID);
    if (p.employeeID !== values.employeeID) onChange("employeeID", values.employeeID);
    prev.current = { ...values };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.companyID, values.branchID, values.employeeID]);

  return null;
}

function MyEnrollments(): React.JSX.Element {
  const navigate = useNavigate();
  const { enrollments, loading, getMyEnrollments } = useCourse();
  const { user, isAdmin, isCompanyAdmin, isFellowAdmin, isCompany, isManager, isHR, isCreator } = useAuth();

  const userId = user?.userId || user?._id;
  const isAdminEnrollmentsView = isAdmin || isCompanyAdmin || isFellowAdmin;
  const isManagerOrHr = isManager || isHR;

  const [companies, setCompanies] = useState<UserOption[]>([]);
  const [branches, setBranches] = useState<UserOption[]>([]);
  const [employees, setEmployees] = useState<UserOption[]>([]);

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");

  const filtersFormRef = useRef<any>(null);

  const handleFilterChange = useCallback((name: keyof FilterValues, value: string) => {
    if (name === "companyID") {
      setSelectedCompanyId(value);
      setSelectedBranchId("");
      setSelectedEmployeeId("");
      filtersFormRef.current?.setFieldValue("branchID", "");
      filtersFormRef.current?.setFieldValue("employeeID", "");
    } else if (name === "branchID") {
      setSelectedBranchId(value);
      setSelectedEmployeeId("");
      filtersFormRef.current?.setFieldValue("employeeID", "");
    } else if (name === "employeeID") {
      setSelectedEmployeeId(value);
    }
  }, []);

  const title = isAdminEnrollmentsView ? "Enrollments" : "My Enrollments";

  const enrollmentFilters = useMemo(() => {
    if (isAdminEnrollmentsView) {
      return {
        companyID: selectedCompanyId || undefined,
        branchID: selectedBranchId || undefined,
        employeeID: selectedEmployeeId || undefined,
      };
    }
    if (isCompany || isManagerOrHr) {
      return {
        employeeID: selectedEmployeeId || undefined,
      };
    }
    return undefined;
  }, [isAdminEnrollmentsView, isCompany, isManagerOrHr, selectedCompanyId, selectedBranchId, selectedEmployeeId]);

  useEffect(() => {
    if (isCreator) return;
    getMyEnrollments(enrollmentFilters);
  }, [getMyEnrollments, enrollmentFilters, isCreator]);

  useEffect(() => {
    if (isCreator) return;

    const fetchCompanies = async () => {
      if (!isAdminEnrollmentsView) return;
      const c1 = await userApi.get("/api/users", { params: { role: "company", limit: 200 } });
      const d1 = c1.data?.status ? c1.data.data?.data || [] : [];
      let allCompanies: UserOption[] = d1;

      // Scope company list based on role
      if (isCompanyAdmin) {
        const assigned: string[] = user?.assignedCompanies || [];
        if (assigned.length > 0) {
          allCompanies = allCompanies.filter((c) => assigned.includes(c._id));
        }
      } else if (isFellowAdmin && user?.adminID) {
        try {
          const parentRes = await userApi.get(`/api/users/${user.adminID}`);
          const parentAssigned: string[] = parentRes.data?.data?.assignedCompanies || [];
          if (parentAssigned.length > 0) {
            allCompanies = allCompanies.filter((c) => parentAssigned.includes(c._id));
          }
        } catch {
          allCompanies = [];
        }
      }

      setCompanies(allCompanies);
    };

    const fetchEmployeesForCompanyScope = async () => {
      if (!(isCompany || isManagerOrHr)) return;
      const companyID = isCompany ? userId : (user?.companyID || userId);
      const branchID = isManagerOrHr ? user?.branchID : undefined;
      const baseParams: any = { companyID, limit: 200 };
      if (branchID) baseParams.branchID = branchID;

      const e1 = await userApi.get("/api/users", { params: { ...baseParams, role: "employee" } });
      const d1 = e1.data?.status ? e1.data.data?.data || [] : [];
      setEmployees(d1);
    };

    fetchCompanies().catch(() => setCompanies([]));
    fetchEmployeesForCompanyScope().catch(() => setEmployees([]));
  }, [isCreator, isAdminEnrollmentsView, isCompany, isManagerOrHr, user?.branchID, user?.companyID, userId]);

  useEffect(() => {
    if (!isAdminEnrollmentsView || !selectedCompanyId) {
      setBranches([]);
      return;
    }

    const fetchBranches = async () => {
      const response = await userApi.get("/api/users", {
        params: { companyID: selectedCompanyId, role: "branch", limit: 200 },
      });
      setBranches(response.data?.status ? response.data.data?.data || [] : []);
    };

    fetchBranches().catch(() => setBranches([]));
  }, [isAdminEnrollmentsView, selectedCompanyId]);

  useEffect(() => {
    if (!isAdminEnrollmentsView || !selectedCompanyId) {
      if (isAdminEnrollmentsView) setEmployees([]);
      return;
    }

    const fetchEmployees = async () => {
      const baseParams: any = { companyID: selectedCompanyId, limit: 200 };
      if (selectedBranchId) baseParams.branchID = selectedBranchId;

      const e1 = await userApi.get("/api/users", { params: { ...baseParams, role: "employee" } });
      const d1 = e1.data?.status ? e1.data.data?.data || [] : [];
      setEmployees(d1);
    };

    fetchEmployees().catch(() => setEmployees([]));
  }, [isAdminEnrollmentsView, selectedCompanyId, selectedBranchId]);

  if (isCreator) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box py={3}>
          <EmptyStateCard
            title="You do not have access to enrollments."
            titleColor="error"
          />
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={0}>
        <PageHeader
          title={title}
          actions={(
            <Button variant="gradient" color="info" size="medium" onClick={() => navigate("/courses")}> 
              <School sx={{ mr: 1 }} /> Browse Courses
            </Button>
          )}
        />

        {(isAdminEnrollmentsView || isCompany || isManagerOrHr) && (
          <Box mb={3} width="100%" minWidth={0}>
            <Formik
              innerRef={filtersFormRef}
              initialValues={{ companyID: "", branchID: "", employeeID: "" }}
              onSubmit={() => {}}
            >
              <Form>
                <FiltersObserver onChange={handleFilterChange} />
                <FilterBar>
                  {isAdminEnrollmentsView && (
                    <FilterItem>
                      <FormikInput
                        name="companyID"
                        type="dropdown"
                        placeholder="Filter by Company"
                        options={[{ _id: "", name: "All Companies" }, ...companies.map((c) => ({ _id: c._id, name: c.name || c.email || c._id }))]}
                        optionsLabel="name"
                        size="small"
                      />
                    </FilterItem>
                  )}
                  {isAdminEnrollmentsView && selectedCompanyId && (
                    <FilterItem>
                      <FormikInput
                        name="branchID"
                        type="dropdown"
                        placeholder="Filter by Branch"
                        options={[{ _id: "", name: "All Branches" }, ...branches.map((b) => ({ _id: b._id, name: b.name || b.email || b._id }))]}
                        optionsLabel="name"
                        size="small"
                      />
                    </FilterItem>
                  )}
                  {(isAdminEnrollmentsView ? !!selectedCompanyId : (isCompany || isManagerOrHr)) && (
                    <FilterItem>
                      <FormikInput
                        name="employeeID"
                        type="dropdown"
                        placeholder="Filter by Employee"
                        options={[{ _id: "", name: "All / Mine" }, ...employees.map((e) => ({ _id: e._id, name: e.name || e.email || e._id }))]}
                        optionsLabel="name"
                        size="small"
                      />
                    </FilterItem>
                  )}
                </FilterBar>
              </Form>
            </Formik>
          </Box>
        )}

        {loading ? (
          <Box minHeight={260} display="flex" alignItems="center" justifyContent="center" {...({} as any)}>
            <Loader loading={true} {...({} as any)} />
          </Box>
        ) : enrollments.length === 0 ? (
          <EmptyStateCard
            icon={<School sx={{ fontSize: 64, color: "text.secondary" }} />}
            title="No enrollments yet"
            description="Start learning by enrolling in courses"
          />
        ) : (
          <Grid container spacing={3}>
            {enrollments.map((enrollment: any) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={enrollment.id}>
                {(() => {
                  const canContinueLearning = Boolean(userId && enrollment.userID && enrollment.userID === userId);

                  return (
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                  }}
                  onClick={() => navigate(`/courses/${enrollment.courseID}`)}
                >
                  <Box p={2} flex={1} display="flex" flexDirection="column" {...({} as any)}>
                    {enrollment.course ? (
                      <>
                        {enrollment.course.thumbnailURL && (
                          <Box
                            sx={{
                              width: "100%",
                              height: 160,
                              mb: 1.5,
                              borderRadius: 1,
                              overflow: "hidden",
                              backgroundColor: "grey.100",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            {...({} as any)}
                          >
                            <img
                              src={`${COMPCHECK_URL}/api/${enrollment.course.thumbnailURL}`}
                              alt={enrollment.course.title}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = "none";
                              }}
                            />
                          </Box>
                        )}

                        <Typography variant="h6" fontWeight="bold" mb={1} {...({} as any)}>
                          {enrollment.course.title}
                        </Typography>
                        {(isAdminEnrollmentsView || isCompany || isManagerOrHr) && enrollment.userName && (
                          <Typography variant="caption" color="text.secondary" mb={1} {...({} as any)}>
                            Learner: {enrollment.userName}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          color="text"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", flex: 1 }}
                          {...({} as any)}
                        >
                          {enrollment.course.description || "No description available"}
                        </Typography>
                        <Box mt={2} mb={2}>
                          <CourseProgressBar label="Progress" value={Number(enrollment.progress || 0)} />
                          {enrollment.status && (
                            <Box mt={1}>
                              <Chip size="small" label={enrollment.status} color={enrollment.status === "completed" ? "success" : "info"} />
                            </Box>
                          )}
                        </Box>
                        <Box mt={2} {...({} as any)}>
                          <Button
                            variant="outlined"
                            color="info"
                            size="small"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/courses/${enrollment.courseID}`);
                            }}
                          >
                            <Visibility sx={{ mr: 0.5, fontSize: "16px" }} />
                            {canContinueLearning ? "Continue Learning" : "View Course"}
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" {...({} as any)}>
                        Course details loading...
                      </Typography>
                    )}
                  </Box>
                </Card>
                  );
                })()}
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}

export default MyEnrollments;
